'use client';

import React, { useState, useCallback, useMemo, useEffect, startTransition } from "react";
import Image from "next/image";
import Icon from "@/components/ui/AppIcon";
import Link from "next/link";

interface ServiceItem {
    id: number;
    title: string;
    description: string;
    icon: string;
    features: string[];
    link: string;
    image: string;
    gradient: string;
    accent: string;
    bg: string;
}

interface Carousel3DProps {
    items: ServiceItem[];
    cardWidth?: number;
    cardHeight?: number;
    gap?: number;
    scaleStep?: number;
    zStep?: number;
    perspective?: number;
}

export default function Carousel3D({
    items,
    cardWidth = 450,
    cardHeight = 650,
    gap = 40,
    scaleStep = 0.12,
    zStep = 80,
    perspective = 900,
}: Carousel3DProps) {
    const [index, setIndex] = useState(0);
    const [buttonDown, setButtonDown] = useState("");
    const total = items.length;

    const handlePrev = useCallback(() => {
        setIndex((i) => (i - 1 + total) % total);
    }, [total]);

    const handleNext = useCallback(() => {
        setIndex((i) => (i + 1) % total);
    }, [total]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") {
                e.preventDefault();
                handlePrev();
            } else if (e.key === "ArrowRight") {
                e.preventDefault();
                handleNext();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handlePrev, handleNext]);

    // Scroll Navigation logic
    useEffect(() => {
        const container = document.getElementById('carousel-container');
        if (!container) return;

        let isAnimating = false;
        let scrollAccumulator = 0;
        const SCROLL_THRESHOLD = 50; // Pixels to scroll before triggering
        let resetTimer: NodeJS.Timeout;

        const handleWheel = (e: WheelEvent) => {
            // Only handle vertical scrolling
            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

            // If already animating, prevent default but don't accumulate
            if (isAnimating) {
                e.preventDefault();
                return;
            }

            // Check boundaries first - if we are at edge, allow native scroll
            if ((e.deltaY > 0 && index === total - 1) || (e.deltaY < 0 && index === 0)) {
                return; // Let default scroll happen
            }

            e.preventDefault(); // Hijack scroll inside the component

            // Accumulate scroll delta
            scrollAccumulator += e.deltaY;

            // Clear previous reset timer
            clearTimeout(resetTimer);

            // Check threshold
            if (Math.abs(scrollAccumulator) > SCROLL_THRESHOLD) {
                if (scrollAccumulator > 0) {
                    // Scroll Down -> Next
                    if (index < total - 1) {
                        isAnimating = true;
                        setIndex(prev => prev + 1);
                        setTimeout(() => {
                            isAnimating = false;
                            scrollAccumulator = 0;
                        }, 800); // Cooldown
                    }
                } else {
                    // Scroll Up -> Prev
                    if (index > 0) {
                        isAnimating = true;
                        setIndex(prev => prev - 1);
                        setTimeout(() => {
                            isAnimating = false;
                            scrollAccumulator = 0;
                        }, 800); // Cooldown
                    }
                }
            } else {
                // If threshold not met, reset accumulator after short pause
                resetTimer = setTimeout(() => {
                    scrollAccumulator = 0;
                }, 200);
            }
        };

        container.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheel);
            clearTimeout(resetTimer);
        };
    }, [index, total]);

    const cards = useMemo(() => {
        return items.map((item, i) => {
            // Calculate relative position to the front card
            let rel = i - index;

            // Wrap around for circular effect
            if (rel < -Math.floor(total / 2)) rel += total;
            if (rel > Math.floor(total / 2)) rel -= total;

            const absRel = Math.abs(rel);

            // 3D illusion calculations
            const z = -absRel * zStep;
            const scale = 1 / (1 + absRel * scaleStep);
            const x = rel * gap;
            const opacity = rel === 0 ? 1 : 0.7 - 0.1 * absRel;
            const pointer = rel === 0 ? "auto" : "none";

            // Floating effect
            const y = rel * 20;
            const rotate = rel * 5;

            return (
                <div
                    key={item.id}
                    style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: "100%", // Responsive width logic handled by parent or max-width
                        maxWidth: cardWidth,
                        height: cardHeight,
                        transform: `translate(-50%, -50%) translateX(${x}px) translateY(${y}px) translateZ(${z}px) scale(${scale}) rotateZ(${rotate}deg)`,
                        zIndex: 100 - absRel,
                        boxShadow: rel === 0 ? "0 25px 50px -12px rgba(0,0,0,0.15)" : "0 10px 15px -3px rgba(0,0,0,0.05)",
                        borderRadius: 36,
                        overflow: "hidden",
                        opacity,
                        transition: "transform 0.5s cubic-bezier(.4,2,.3,1), opacity 0.4s",
                        background: "#fff",
                        cursor: rel === 0 ? "default" : "pointer",
                        pointerEvents: pointer as any,
                    }}
                    onClick={() => {
                        if (rel !== 0) {
                            // If clicking a side card, scroll to it
                            // Calculate direction
                            if (rel > 0) handleNext();
                            else handlePrev();
                        }
                    }}
                >
                    {/* Content Card */}
                    <div className="relative w-full h-full flex flex-col bg-white border border-slate-200">
                        {/* Background Image */}
                        <div className="absolute inset-0 z-0 h-2/5">
                            <Image
                                src={item.image}
                                alt={item.title}
                                fill
                                className="object-cover opacity-90"
                            />
                            <div className={`absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent`} />
                        </div>

                        {/* Header */}
                        <div className="relative z-10 p-8 flex items-start justify-between mt-24">
                            <div>
                                <div className={`inline-flex items-center justify-center p-3 rounded-xl bg-gradient-to-br ${item.gradient} shadow-lg mb-4`}>
                                    <Icon name={item.icon as any} className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-3xl font-bold text-slate-900 leading-tight">
                                    {item.title}
                                </h3>
                            </div>
                            <div className="text-6xl font-black text-slate-100">0{item.id}</div>
                        </div>

                        {/* Body */}
                        <div className="relative z-10 px-8 pb-4 flex-1 flex flex-col justify-center">
                            <p className="text-slate-600 text-base md:text-lg mb-6 leading-relaxed line-clamp-4">
                                {item.description}
                            </p>

                            {/* Features List */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                                {item.features.map((feature, idx) => (
                                    <div key={idx} className="flex items-center gap-2 group/feat">
                                        <div className={`w-1.5 h-1.5 rounded-full ${item.accent.replace('text-', 'bg-')} group-hover/feat:scale-150 transition-transform`} />
                                        <span className="text-slate-700 text-sm font-medium">{feature}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="relative z-10 p-6 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
                            <Link href={item.link} className={`flex items-center gap-2 ${item.accent} font-bold hover:gap-4 transition-all uppercase tracking-wider text-sm`}>
                                Explore
                                <Icon name="ArrowRightIcon" className="w-4 h-4" />
                            </Link>

                            {/* Mobile Swipe Indicators for context */}
                            <div className="flex gap-1">
                                {items.map((_, dotIdx) => (
                                    <div
                                        key={dotIdx}
                                        className={`w-1.5 h-1.5 rounded-fullTransition ${dotIdx === i ? 'bg-slate-800 w-4' : 'bg-slate-300'}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            );
        });
    }, [items, index, cardWidth, cardHeight, gap, scaleStep, zStep, total, handleNext, handlePrev]);

    return (
        <div
            id="carousel-container"
            style={{
                width: "100%",
                height: "100%",
                position: "relative",
                perspective: `${perspective}px`,
                overflow: "hidden", // Changed to hidden to prevent scrollbar flicker
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: cardHeight + 100
            }}
        >
            {/* Nav Buttons */}
            <button
                aria-label="Previous"
                onClick={handlePrev}
                className="absolute left-4 md:left-12 top-1/2 -translate-y-1/2 z-[200] w-12 h-12 md:w-16 md:h-16 rounded-full bg-white/80 backdrop-blur-md border border-slate-200 text-slate-900 flex items-center justify-center hover:bg-white hover:scale-110 transition-all active:scale-95 shadow-xl hover:shadow-2xl"
            >
                <Icon name="ChevronLeftIcon" className="w-6 h-6 md:w-8 md:h-8" />
            </button>

            <button
                aria-label="Next"
                onClick={handleNext}
                className="absolute right-4 md:right-12 top-1/2 -translate-y-1/2 z-[200] w-12 h-12 md:w-16 md:h-16 rounded-full bg-white/80 backdrop-blur-md border border-slate-200 text-slate-900 flex items-center justify-center hover:bg-white hover:scale-110 transition-all active:scale-95 shadow-xl hover:shadow-2xl"
            >
                <Icon name="ChevronRightIcon" className="w-6 h-6 md:w-8 md:h-8" />
            </button>

            <div style={{ width: "100%", height: "100%", position: "relative", perspective: `${perspective}px`, pointerEvents: "none" }}>
                {cards}
            </div>
        </div>
    );
}
